/**
 * Enhanced Manufacturer Registration Page
 * Multi-step registration form for collecting detailed legal information
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyDetailsForm } from '../components/registration/CompanyDetailsForm';
import { TaxInformationForm } from '../components/registration/TaxInformationForm';
import { AuthorizedSignatoryForm } from '../components/registration/AuthorizedSignatoryForm';
import { BankingDetailsForm } from '../components/registration/BankingDetailsForm';
import { BrandInformationForm } from '../components/registration/BrandInformationForm';
import { ComplianceDeclarationsForm } from '../components/registration/ComplianceDeclarationsForm';
import { ReviewSubmitForm } from '../components/registration/ReviewSubmitForm';
import { registrationService, RegistrationSession, RegistrationStep } from '../services/registration.service';

const STEPS: { key: RegistrationStep; title: string; description: string }[] = [
  {
    key: 'company_details',
    title: 'Company Details',
    description: 'Basic company information and addresses'
  },
  {
    key: 'tax_information',
    title: 'Tax Information',
    description: 'Government registration numbers (GSTIN, PAN, CIN)'
  },
  {
    key: 'authorized_signatory',
    title: 'Authorized Signatory',
    description: 'Person legally responsible for operations'
  },
  {
    key: 'banking_details',
    title: 'Banking Details',
    description: 'Bank account and billing information'
  },
  {
    key: 'brand_information',
    title: 'Brand Information',
    description: 'Brand names, trademarks, and product categories'
  },
  {
    key: 'declarations',
    title: 'Legal Declarations',
    description: 'Terms, privacy policy, and compliance agreements'
  },
  {
    key: 'review_submit',
    title: 'Review & Submit',
    description: 'Review all information and submit for approval'
  }
];

export default function EnhancedRegistrationPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState<RegistrationSession | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRegistrationSession();
  }, []);

  const loadRegistrationSession = async () => {
    try {
      setLoading(true);
      const sessionData = await registrationService.getRegistrationSession();
      setSession(sessionData);
      
      // Set current step based on session data
      const currentStep = sessionData.currentStep;
      const stepIndex = STEPS.findIndex(step => step.key === currentStep);
      setCurrentStepIndex(stepIndex >= 0 ? stepIndex : 0);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStepSave = async (stepData: any) => {
    try {
      setSaving(true);
      setError(null);
      
      const currentStep = STEPS[currentStepIndex];
      await registrationService.updateRegistrationStep(currentStep.key, stepData);
      
      // Reload session to get updated data
      await loadRegistrationSession();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow navigation to completed steps or current step
    if (session && stepIndex <= session.completedSteps.length) {
      setCurrentStepIndex(stepIndex);
    }
  };

  const handleSubmitRegistration = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const result = await registrationService.submitRegistration();
      
      if (result.success) {
        // Show success message and redirect to a pending approval page
        alert('Registration submitted successfully! Your application is now under review. You will receive an email once it\'s approved.');
        // Redirect to a pending approval page instead of dashboard
        navigate('/registration-pending');
      } else {
        setError(result.error || 'Failed to submit registration');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const renderCurrentStep = () => {
    if (!session) return null;

    const currentStep = STEPS[currentStepIndex];
    const stepData = session.data;

    switch (currentStep.key) {
      case 'company_details':
        return (
          <CompanyDetailsForm
            data={stepData.companyDetails}
            onSave={handleStepSave}
            onNext={handleNext}
            saving={saving}
          />
        );
      case 'tax_information':
        return (
          <TaxInformationForm
            data={stepData.taxInformation}
            onSave={handleStepSave}
            onNext={handleNext}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      case 'authorized_signatory':
        return (
          <AuthorizedSignatoryForm
            data={stepData.authorizedSignatory}
            onSave={handleStepSave}
            onNext={handleNext}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      case 'banking_details':
        return (
          <BankingDetailsForm
            data={stepData.bankingDetails}
            onSave={handleStepSave}
            onNext={handleNext}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      case 'brand_information':
        return (
          <BrandInformationForm
            data={stepData.brandInformation}
            onSave={handleStepSave}
            onNext={handleNext}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      case 'declarations':
        return (
          <ComplianceDeclarationsForm
            data={stepData.declarations}
            onSave={handleStepSave}
            onNext={handleNext}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      case 'review_submit':
        return (
          <ReviewSubmitForm
            session={session}
            onSubmit={handleSubmitRegistration}
            onPrevious={handlePrevious}
            saving={saving}
          />
        );
      default:
        return <div>Unknown step</div>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading registration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Complete Your Registration</h1>
        <p className="text-sm text-gray-600 mt-1">
          Provide detailed information to get your account approved
        </p>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const isCompleted = session?.completedSteps.includes(step.key);
              const isCurrent = index === currentStepIndex;
              const isAccessible = session && index <= session.completedSteps.length;

              return (
                <div key={step.key} className="flex items-center">
                  <button
                    onClick={() => handleStepClick(index)}
                    disabled={!isAccessible}
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                      isCompleted
                        ? 'bg-green-100 text-green-600'
                        : isCurrent
                        ? 'bg-blue-100 text-blue-600'
                        : isAccessible
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isCompleted ? '✓' : index + 1}
                  </button>
                  {index < STEPS.length - 1 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-300' : 'bg-gray-300'
                    }`}></div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4">
            <h3 className="text-lg font-medium text-gray-900">
              {STEPS[currentStepIndex].title}
            </h3>
            <p className="text-sm text-gray-600">
              {STEPS[currentStepIndex].description}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Registration Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {renderCurrentStep()}
        </div>
      </section>
    </div>
  );
}
